import {
  Component,
  computed,
  effect,
  ElementRef,
  signal,
  viewChild,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
} from '@angular/router';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { filter } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { TranslationService } from '../../core/services/translation.service';
import { UserService } from '../../core/services/user.service';
import { FabricComparisonBannerComponent } from '../../shared/components/fabric-comparison-banner/fabric-comparison-banner.component';
import { FabricSearchComponent } from '../../shared/components/fabric-search/fabric-search.component';
import { SiteFooterComponent } from '../site-footer/site-footer.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { CartItemService } from '../../shared/services/cart-item.service';
import { QuotationRequestItemService } from '../../shared/services/quotation-request-item.service';

interface NavigationItem {
  label: string;
  icon: string;
  /** Absent on a parent item, which only opens its children rather than navigating. */
  route?: string;
  children?: NavigationItem[];
}

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    NzBadgeModule,
    NzButtonModule,
    NzDropDownModule,
    NzGridModule,
    NzIconModule,
    NzLayoutModule,
    NzMenuModule,
    TranslatePipe,
    FabricComparisonBannerComponent,
    FabricSearchComponent,
    SiteFooterComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
})
export class MainLayoutComponent {
  @ViewChild('contentBody', { read: ElementRef }) contentBody!: ElementRef<HTMLElement>;

  /** Only present while the band is open — the field it focuses on opening. */
  readonly fabricSearch = viewChild(FabricSearchComponent);

  protected readonly i18n: TranslationService;

  /** Whether the search band under the bar is open. */
  readonly searchOpen = signal(false);

  /** Whether the off-canvas navigation drawer is open (compact screens only) */
  readonly isMenuOpen = signal(false);
  readonly isNavigating = signal(false);
  /** True once the content area is scrolled — drives the header glass effect */
  readonly scrolled = signal(false);

  /** Current URL, kept in sync with navigation so grouped nav items can resolve their active state. */
  readonly currentUrl = signal('');
  /** Label of the nav group whose dropdown is open, for `aria-expanded` and the caret. */
  readonly openGroup = signal<string | null>(null);
  /** Whether the account dropdown is open, for `aria-expanded` and the caret. */
  readonly accountMenuOpen = signal(false);

  // One-shot "pop" flags — pulsed when the matching count changes
  readonly cartBump = signal(false);
  readonly quotationBump = signal(false);

  readonly navigationItems: NavigationItem[] = [
    { label: 'Home', icon: 'home', route: '/home' },
    { label: 'Fabrics', icon: 'fabric', route: '/fabrics' },
    { label: 'Collections', icon: 'catalog', route: '/collections' },
    {
      label: 'Requests',
      icon: 'profile',
      children: [
        { label: 'Quotations', icon: 'quotation', route: '/quotations' },
        { label: 'Developments', icon: 'development', route: '/development-requests' },
        { label: 'Orders', icon: 'orders', route: '/orders' },
      ],
    },
    // { label: 'Company Users', icon: 'team', route: '/company-users' },
  ];

  readonly userDisplayName = computed(() => this.authService.currentUser()?.name);
  readonly userInitials = computed(() => this.authService.currentUser()?.initials);

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly cartItemService: CartItemService,
    private readonly quotationRequestItemService: QuotationRequestItemService,
    private readonly userService: UserService,
    i18n: TranslationService
  ) {
    this.i18n = i18n;
    this.currentUrl.set(this.router.url);
    this.cartItemService.countItemsInCart().subscribe();
    this.quotationRequestItemService.countItemsInQuotationCart().subscribe();

    this.bumpOnChange(() => this.cartItemService.count(), this.cartBump);
    this.bumpOnChange(() => this.quotationRequestItemService.count(), this.quotationBump);

    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      this.isNavigating.set(true);
      // At the start, not the end: picking a suggestion should collapse the band at once rather
      // than leave it hanging over the page it is loading. The navigation is already under way.
      this.searchOpen.set(false);
    });

    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError ||
            event instanceof RouteConfigLoadEnd
        )
      )
      .subscribe(() => {
        // Close mobile overlay sidebar on navigation
        this.isMenuOpen.set(false);
        // Scroll to top on every route change
        this.contentBody?.nativeElement?.scrollTo({ top: 0, behavior: 'instant' });
        this.isNavigating.set(false);
        this.currentUrl.set(this.router.url);
      });

    this.router.events
      .pipe(filter((event) => event instanceof RouteConfigLoadStart))
      .subscribe(() => {
        this.isNavigating.set(true);
      });
  }

  toggleMenu(): void {
    this.isMenuOpen.update((v) => !v);
  }

  /**
   * Opens (or closes) the search band and puts the caret in it.
   *
   * The focus waits a macrotask: the field does not exist until change detection has rendered the
   * band, which happens after this handler returns.
   */
  toggleSearch(): void {
    const open = !this.searchOpen();
    this.searchOpen.set(open);

    if (open) {
      setTimeout(() => this.fabricSearch()?.focus());
    }
  }

  closeSearch(): void {
    this.searchOpen.set(false);
  }

  /** Toggle the header glass effect once the content scrolls past the top. */
  onContentScroll(event: Event): void {
    const top = (event.target as HTMLElement)?.scrollTop ?? 0;
    this.scrolled.set(top > 8);
  }

  closeMenu(): void {
    this.isMenuOpen.set(false);
  }

  /**
   * A grouped item has no route of its own, so `routerLinkActive` can't drive it — it is
   * active whenever one of its children matches the current URL.
   */
  isGroupActive(item: NavigationItem): boolean {
    const path = this.currentUrl().split(/[?#]/)[0];
    return !!item.children?.some(
      (child) => !!child.route && (path === child.route || path.startsWith(`${child.route}/`))
    );
  }

  /** Mirrors the dropdown's open state so the trigger can expose `aria-expanded` and rotate its caret. */
  onGroupVisibleChange(item: NavigationItem, visible: boolean): void {
    this.openGroup.set(visible ? item.label : null);
  }

  logout(): void {
    this.authService.logout().subscribe();
  }

  setLanguage(language: 'en' | 'pt'): void {
    if (this.i18n.language() === language) {
      return;
    }

    this.i18n.setLanguage(language);

    // Persist the choice for logged-in users only — drives auto-resolved reads
    // and transactional email language server-side. On success, mirror it onto the
    // in-session user so `defaultLanguage` stays consistent without a re-fetch.
    // Anonymous visitors keep the client-only (localStorage) preference.
    if (this.authService.isAuthenticated()) {
      this.userService
        .updateLanguage(language)
        .subscribe(() => this.authService.setDefaultLanguage(language));
    }
  }

  get cartCount(): number {
    return this.cartItemService.count();
  }

  get quotationCartCount(): number {
    return this.quotationRequestItemService.count();
  }

  /** Pulse a badge's bump flag whenever its count changes after the initial load. */
  private bumpOnChange(count: () => number, bump: WritableSignal<boolean>): void {
    let previous: number | undefined;
    effect(() => {
      const value = count();
      if (previous !== undefined && value !== previous) {
        bump.set(true);
        setTimeout(() => bump.set(false), 500);
      }
      previous = value;
    });
  }
}
